/* Centralized workflow registry. Single source of truth for:
   - Stage order & labels per domain
   - Side-branch / terminal states (rejected, declined, etc.)
   - Tone tokens (color + label) for any status value
   Used by <WorkflowStrip /> and <StatusPill />. */

/* Civic / editorial tone tokens — match the rest of the app. */
export const TONES = {
  // Forward / active
  indigo: { bg: "#1B1F4B", fg: "#FBFAFD", soft: "#E6E7F1" },
  // Completed / success
  green: { bg: "#4F6149", fg: "#FBFAFD", soft: "#E5EBE2" },
  // Caution / awaiting info
  amber: { bg: "#B98A3C", fg: "#272636", soft: "#F4EAD2" },
  // Stop / failure
  red: { bg: "#A23B2E", fg: "#FBFAFD", soft: "#F2DAD4" },
  // Neutral / draft / dormant
  slate: { bg: "#9AA0C0", fg: "#272636", soft: "#ECEBF1" },
  // Subtle / muted (sand)
  sand: { bg: "#5B6196", fg: "#FBFAFD", soft: "#E6E7F1" },
};

/* Each workflow defines an ordered list of `stages` (the "happy path")
   and an optional `branches` map for off-path / halted states. */
export const WORKFLOWS = {
  project: {
    label: "Project lifecycle",
    description: "Selection through completion of construction.",
    stages: [
      { key: "Selection", label: "Selection" },
      { key: "Consultation", label: "Consultation" },
      { key: "Purchase", label: "Purchase" },
      { key: "Expedited Review", label: "Expedited Review" },
      { key: "Build", label: "Build" },
      { key: "Construction Inspection", label: "Inspection" },
      { key: "Complete", label: "Complete" },
    ],
    branches: {},
  },

  consultation: {
    label: "Consultation",
    description: "Requested → Activated as a project.",
    stages: [
      { key: "Consultation Requested", label: "Requested" },
      { key: "In Consultation", label: "In Consultation" },
      { key: "Activated", label: "Activated" },
    ],
    branches: {
      Declined: {
        afterStage: "In Consultation",
        tone: "red",
        label: "Declined",
      },
    },
  },

  application: {
    label: "Architect application",
    description: "Submitted → Approved into the marketplace.",
    stages: [
      { key: "pending_verification", label: "Pending verification" },
      { key: "approved", label: "Approved" },
    ],
    branches: {
      more_info_requested: {
        afterStage: "pending_verification",
        tone: "amber",
        label: "More info requested",
      },
      rejected: {
        afterStage: "pending_verification",
        tone: "red",
        label: "Rejected",
      },
    },
  },

  design: {
    label: "Design review",
    description: "Draft → Approved for the marketplace.",
    stages: [
      { key: "Draft", label: "Draft" },
      { key: "In Review", label: "In Review" },
      { key: "Approved", label: "Approved" },
    ],
    branches: {
      "Needs Revision": {
        afterStage: "In Review",
        tone: "amber",
        label: "Needs Revision",
      },
      Rejected: {
        afterStage: "In Review",
        tone: "red",
        label: "Rejected",
      },
    },
  },
};

/* Global pill tone lookup. Values may be raw backend strings; we map any
   recognized status to a tone + display label. Falls back to neutral slate. */
const PILL_REGISTRY = {
  // Project lifecycle
  Selection:                 { tone: "sand",   label: "Selection" },
  Consultation:              { tone: "sand",   label: "Consultation" },
  Purchase:                  { tone: "indigo", label: "Purchase" },
  "Expedited Review":        { tone: "indigo", label: "Expedited Review" },
  Build:                     { tone: "indigo", label: "Build" },
  "Construction Inspection": { tone: "indigo", label: "Inspection" },
  Complete:                  { tone: "green",  label: "Complete" },

  // Consultation
  "Consultation Requested":  { tone: "amber",  label: "Requested" },
  "In Consultation":         { tone: "indigo", label: "In Consultation" },
  Activated:                 { tone: "green",  label: "Activated" },
  Declined:                  { tone: "red",    label: "Declined" },

  // Application
  pending_verification:      { tone: "indigo", label: "Pending verification" },
  more_info_requested:       { tone: "amber",  label: "More info requested" },
  approved:                  { tone: "green",  label: "Approved" },
  rejected:                  { tone: "red",    label: "Rejected" },

  // Design review
  Draft:                     { tone: "slate",  label: "Draft" },
  "In Review":               { tone: "amber",  label: "In Review" },
  Approved:                  { tone: "green",  label: "Approved" },
  "Needs Revision":          { tone: "amber",  label: "Needs Revision" },
  Rejected:                  { tone: "red",    label: "Rejected" },

  // Parcel marketplace
  Available:                 { tone: "green",  label: "Available" },
  Reserved:                  { tone: "amber",  label: "Reserved" },
  Sold:                      { tone: "slate",  label: "Sold" },

  // Permit / Accela
  in_review:                 { tone: "indigo", label: "In Review" },
  permitted:                 { tone: "green",  label: "Permitted" },
  issued:                    { tone: "green",  label: "Issued" },
  under_construction:        { tone: "indigo", label: "Under Construction" },
  permit_issued:             { tone: "green",  label: "Permit Issued" },
  sync_error:                { tone: "red",    label: "Sync Error" },

  // Architect account
  verified_active:           { tone: "green",  label: "Verified · Active" },
  profile_published:         { tone: "green",  label: "Published" },
  profile_unpublished:       { tone: "slate",  label: "Unpublished" },
  deactivated:               { tone: "red",    label: "Deactivated" },
  active:                    { tone: "green",  label: "Active" },
  pending:                   { tone: "amber",  label: "Pending" },
};

export function getStatusTone(value) {
  if (!value) return { tone: "slate", label: "—", ...TONES.slate };
  const entry = PILL_REGISTRY[value] || { tone: "slate", label: value };
  return { ...entry, ...TONES[entry.tone] };
}

/* Returns { stageIdx, branch, isHalted } describing where `currentStatus`
   sits in the workflow. `stageIdx === -1` when status isn't on the happy
   path (e.g., currently on a side branch). */
export function resolveWorkflowPosition(domain, currentStatus) {
  const wf = WORKFLOWS[domain];
  if (!wf) return { stageIdx: -1, branch: null, isHalted: false, stages: [] };
  const idx = wf.stages.findIndex((s) => s.key === currentStatus);
  const branch = wf.branches?.[currentStatus] || null;
  return {
    stageIdx: idx,
    branch,
    isHalted: !!branch,
    stages: wf.stages,
    branches: wf.branches || {},
    workflow: wf,
  };
}
